import { formatTeamRosterRoleDisplay } from './teamRosterRoleDisplay';

export type BugOriginBarSegment = { key: string; label: string; color: string };

export type BugOriginReporterBucket = 'qa' | 'developer' | 'support';

/**
 * Legacy name-heuristic buckets when the product has no `team_members` rows.
 * Must stay aligned with `resolveReporterRole(..., null)` in `server/services/teamRosterLookup.js`.
 */
export function classifyBugOriginReporter(
  reporter: string | null | undefined
): BugOriginReporterBucket {
  const name = String(reporter || '').toLowerCase();
  if (/qa|tester|quality/.test(name)) return 'qa';
  if (/support|customer|cs|client/.test(name)) return 'support';
  return 'developer';
}

/** Same slug rules as `slugifyRole` in `server/services/teamRosterLookup.js`. */
export function slugifyBugOriginRole(role: string): string {
  const base = String(role || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return `r_${base || 'unknown'}`;
}

const LEGACY_SUPPORT_LABEL = 'Support / Customer';

/** Fallback series when `diagnostics.bugOriginSeries` is missing (older API). */
export const LEGACY_BUG_ORIGIN_CHART_SERIES: ReadonlyArray<{
  key: string;
  label: string;
  color: string;
}> = [
  { key: 'qa', label: formatTeamRosterRoleDisplay('qa'), color: '#0ea5e9' },
  { key: 'developer', label: formatTeamRosterRoleDisplay('developer'), color: '#22c55e' },
  { key: 'support', label: LEGACY_SUPPORT_LABEL, color: '#ef4444' },
];

/** @deprecated Prefer `resolveBugOriginSeries(diagnostics)` — keys differ for roster-driven charts. */
export const BUG_ORIGIN_CHART_SERIES = LEGACY_BUG_ORIGIN_CHART_SERIES;

export function resolveBugOriginSeries(
  diagnostics: { bugOriginSeries?: BugOriginBarSegment[] } | null | undefined
): BugOriginBarSegment[] {
  const s = diagnostics?.bugOriginSeries;
  if (s && s.length > 0) return s;
  return [...LEGACY_BUG_ORIGIN_CHART_SERIES];
}

export function bugOriginReporterColor(
  reporterRole: string | null | undefined,
  series: readonly BugOriginBarSegment[]
): string | undefined {
  const r = String(reporterRole || '').trim();
  if (!r) return undefined;
  const slug = slugifyBugOriginRole(r);
  const hit = series.find((e) => e.key === slug || e.key === r || e.key === r.toLowerCase());
  return hit?.color;
}

export function bugOriginReporterCellClass(
  _reporter: string | null | undefined,
  reporterRole?: string | null | undefined
): string | undefined {
  const b = String(reporterRole || '')
    .trim()
    .toLowerCase();
  if (!b) return undefined;
  if (b === 'qa') return 'bucket-drilldown-reporter--qa';
  if (b === 'support') return 'bucket-drilldown-reporter--support';
  if (b === 'developer' || b === 'dev') return 'bucket-drilldown-reporter--dev';
  return 'bucket-drilldown-reporter--roster';
}
